/**
 * Timeblock Capacity Calculation Utilities
 *
 * Provides functions for calculating how full a timeblock is based on
 * scheduled tasks and their time estimates.
 */

/**
 * Parse time estimate string to minutes
 * @param {string|null} timeEstimate - '5', '15', '30', '60', '120', '240' or null
 * @returns {number} Minutes (defaults to 15 if null/undefined)
 */
export function parseTimeEstimate(timeEstimate) {
  if (!timeEstimate) return 15; // Default 15 minutes per user preference
  const parsed = parseInt(timeEstimate, 10);
  return isNaN(parsed) ? 15 : parsed;
}

/**
 * Calculate timeblock duration in minutes from Google Calendar event
 * @param {Object} event - Google Calendar event with start.dateTime and end.dateTime
 * @returns {number} Duration in minutes
 */
export function getBlockDurationMinutes(event) {
  if (!event?.start?.dateTime || !event?.end?.dateTime) return 0;
  const start = new Date(event.start.dateTime);
  const end = new Date(event.end.dateTime);
  return Math.round((end - start) / (1000 * 60));
}

/**
 * Calculate total scheduled time for tasks in a block
 * @param {Array} tasks - Array of tasks with timeEstimate field
 * @returns {number} Total minutes scheduled
 */
export function getTotalScheduledMinutes(tasks) {
  if (!tasks || !Array.isArray(tasks)) return 0;
  return tasks.reduce((sum, task) => {
    return sum + parseTimeEstimate(task.timeEstimate);
  }, 0);
}

/**
 * Calculate capacity metrics for a timeblock
 * @param {Object} event - Google Calendar event
 * @param {Array} tasks - Tasks assigned to this block
 * @returns {Object} { totalMinutes, scheduledMinutes, remainingMinutes, fillPercentage, isOverbooked }
 */
export function calculateBlockCapacity(event, tasks) {
  const totalMinutes = getBlockDurationMinutes(event);
  const scheduledMinutes = getTotalScheduledMinutes(tasks);
  const remainingMinutes = totalMinutes - scheduledMinutes;
  const fillPercentage = totalMinutes > 0
    ? Math.min(100, Math.round((scheduledMinutes / totalMinutes) * 100))
    : 0;

  return {
    totalMinutes,
    scheduledMinutes,
    remainingMinutes,
    fillPercentage,
    isOverbooked: scheduledMinutes > totalMinutes
  };
}

/**
 * Format minutes for display
 * @param {number} minutes
 * @returns {string} e.g., "45 min" or "1h 30m"
 */
function formatMinutes(minutes) {
  const absMinutes = Math.abs(minutes);
  if (absMinutes < 60) return `${absMinutes} min`;
  const hours = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Format remaining capacity for display
 * @param {number} remainingMinutes
 * @returns {string} e.g., "45 min left" or "Overbooked by 15 min"
 */
export function formatRemainingCapacity(remainingMinutes) {
  if (remainingMinutes < 0) {
    return `Overbooked by ${formatMinutes(remainingMinutes)}`;
  }
  return `${formatMinutes(remainingMinutes)} left`;
}

/**
 * Get fill color based on capacity percentage
 * @param {number} fillPercentage - 0-100+ percentage
 * @param {boolean} isOverbooked - Whether the block is overbooked
 * @returns {string} Color hex code
 */
export function getFillColor(fillPercentage, isOverbooked) {
  if (isOverbooked) return '#EF4444'; // red-500
  if (fillPercentage > 80) return '#F97316'; // orange-500
  if (fillPercentage > 50) return '#F59E0B'; // amber-500
  return '#22C55E'; // green-500
}
